import type { SessionTrigger } from '@developerz/domain';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import type { FleetRow } from '../lib/overview';
import { absolute, since } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';

// Agent-fleet table (Home middle row, left panel). Presenter — the container
// passes repo × box-run × latest-session rows already joined by `toFleetRows`.
// Status is an outline chip with a leading tone dot; repository links into the
// repo workspace; UPDATED is the compact mono age ("12s", "2m"). A working or
// active row is the table's `live` tone.
//
// "Working" is said ONLY of a repo a box holds a run for. A session in flight with
// no box is worded by what it does (`ACTIVE_KEY`), never "Working" (`FleetRow`).

const STATUS_KEY: Record<Exclude<FleetRow['status'], 'active'>, string> = {
  working: 'overview.status_working',
  idle: 'overview.status_idle',
  offline: 'overview.status_offline',
};

/** A worker-side session in flight, by its trigger; any other trigger reads "Active". */
const ACTIVE_KEY: Record<SessionTrigger, string> = {
  issue: 'overview.status_triaging',
  pr: 'overview.status_reviewing',
  release: 'overview.status_releasing',
  manual: 'overview.status_active',
};

function statusLabel(row: FleetRow): string {
  if (row.status !== 'active') return t(STATUS_KEY[row.status]);
  const key = ACTIVE_KEY[row.trigger as SessionTrigger] ?? 'overview.status_active';
  return t(key);
}

/** The box-held task's title, else latest trigger → human task label; unknown triggers verbatim. */
function taskLabel(row: FleetRow): string {
  if (row.task !== null) return row.task;
  if (row.trigger === null) return UNSET;
  const key = `overview.trigger_${row.trigger}`;
  return t.has(key) ? t(key) : row.trigger;
}

export function FleetTable(props: { rows: readonly FleetRow[] }): JSX.Element {
  const columns: readonly DataTableColumn<FleetRow>[] = [
    {
      key: 'repo',
      rowHeader: true,
      header: t('overview.col_repo'),
      mono: true,
      cell: (row) => (
        <A
          class="home-table__repo"
          href={orgHref(`/repos/${encodeURIComponent(row.repo_full_name)}`)}
        >
          {row.repo_full_name}
        </A>
      ),
    },
    { key: 'task', header: t('overview.col_task'), cell: (row) => taskLabel(row) },
    {
      key: 'status',
      header: t('overview.col_status'),
      cell: (row) => (
        <span class={`badge badge--outline status fleet-status fleet-status--${row.status}`}>
          <span class="fleet-status__dot" aria-hidden="true" />
          {statusLabel(row)}
        </span>
      ),
    },
    {
      key: 'updated',
      header: t('overview.col_updated'),
      class: 'home-table__updated',
      cell: (row) => (
        <Show when={row.updated_iso} fallback={UNSET}>
          {(iso) => (
            <time datetime={iso()} title={absolute(iso())}>
              {since(iso())}
            </time>
          )}
        </Show>
      ),
    },
  ];

  return (
    <DataTable
      label={t('overview.fleet_table_label')}
      class="home-table"
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.repo_full_name}
      rowTone={(row) => (row.status === 'working' || row.status === 'active' ? 'live' : undefined)}
    />
  );
}
